/**
 * Assainissement du HTML avant injection.
 *
 * Certains documents arrivent chez nous en DOCX déposé par un tiers — un
 * sourceur, un candidat — et sont convertis en HTML dans le navigateur pour
 * l'aperçu. Ce HTML est ensuite injecté : c'est le seul endroit du front où du
 * contenu extérieur devient du balisage exécutable.
 *
 * Le convertisseur n'est PAS un assainisseur : il transpose une mise en forme,
 * il ne se donne pas pour mission de bloquer une attaque. La seule protection
 * est ici, et elle marche par liste blanche — ce qui n'est pas explicitement
 * autorisé disparaît. Une liste noire laisserait passer ce qu'on n'a pas prévu.
 */

/* Ce qu'un document de traitement de texte produit légitimement. */
const BALISES = new Set([
  'P', 'BR', 'HR', 'SPAN', 'DIV',
  'STRONG', 'B', 'EM', 'I', 'U', 'S', 'SUP', 'SUB', 'SMALL', 'MARK',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'UL', 'OL', 'LI', 'DL', 'DT', 'DD',
  'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH', 'CAPTION', 'COLGROUP', 'COL',
  'A', 'IMG', 'BLOCKQUOTE', 'PRE', 'CODE', 'FIGURE', 'FIGCAPTION',
]);
/* Retirées AVEC leur contenu : leur texte n'a rien à faire à l'écran. */
const VIDER = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'BASE', 'SVG', 'MATH', 'TEMPLATE', 'NOSCRIPT']);
/* Attributs conservés, par balise. Tout le reste tombe — y compris les
   gestionnaires « on* » et les styles, qui peuvent masquer ou repositionner. */
const ATTRS = {
  A: ['href', 'title'],
  IMG: ['src', 'alt', 'width', 'height'],
  TD: ['colspan', 'rowspan'],
  TH: ['colspan', 'rowspan'],
  COL: ['span'],
  COLGROUP: ['span'],
};

const urlSure = (v, { imageEnLigne = false } = {}) => {
  const brut = String(v || '').trim();
  // Un schéma relatif ou un ancrage restent inoffensifs.
  if (!brut || brut.startsWith('#') || brut.startsWith('/')) return brut || null;
  // Les images d'un DOCX arrivent en data: — c'est le cas normal, et une
  // image de données ne s'exécute pas. Tout autre data: est refusé.
  if (imageEnLigne && /^data:image\/(png|jpe?g|gif|webp|bmp);base64,/i.test(brut)) return brut;
  return /^(https?:|mailto:|tel:)/i.test(brut) ? brut : null;
};

/**
 * Rend un HTML sûr à injecter. Chaîne vide si l'environnement n'a pas de
 * DOMParser — mieux vaut ne rien afficher qu'injecter sans avoir vérifié.
 */
export function htmlSur(html) {
  const brut = String(html || '');
  if (!brut.trim()) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser !== 'function') return '';

  const doc = new window.DOMParser().parseFromString(brut, 'text/html');

  const nettoyer = (noeud) => {
    [...noeud.childNodes].forEach((enfant) => {
      if (enfant.nodeType === 8) { enfant.remove(); return; }        // commentaire
      if (enfant.nodeType !== 1) return;                              // texte : gardé tel quel
      const tag = enfant.tagName.toUpperCase();

      if (VIDER.has(tag)) { enfant.remove(); return; }
      if (!BALISES.has(tag)) {
        // Balise inconnue : on garde son texte, on jette l'enveloppe. Une
        // suppression pure perdrait du contenu légitime d'un CV.
        nettoyer(enfant);
        enfant.replaceWith(...enfant.childNodes);
        return;
      }

      const permis = ATTRS[tag] || [];
      [...enfant.attributes].forEach((a) => {
        const nom = a.name.toLowerCase();
        if (!permis.includes(nom)) { enfant.removeAttribute(a.name); return; }
        if (nom === 'href' || nom === 'src') {
          const sur = urlSure(a.value, { imageEnLigne: tag === 'IMG' });
          if (sur) enfant.setAttribute(nom, sur); else enfant.removeAttribute(a.name);
        }
      });
      // Un lien d'un document tiers s'ouvre détaché de l'application.
      if (tag === 'A' && enfant.getAttribute('href')) {
        enfant.setAttribute('target', '_blank');
        enfant.setAttribute('rel', 'noopener noreferrer');
      }
      nettoyer(enfant);
    });
  };

  nettoyer(doc.body);
  return doc.body.innerHTML;
}

export default htmlSur;
